import type { HelpArticle, HelpUpload, HelpUploadTarget } from "./types";
import { canonicalHelpMediaType, validateHelpBundle } from "./file_validation";

export type HelpArticleMetadata = {
  title: string;
  slug: string;
  summary: string;
  category: string;
};

export type HelpFileManifestItem = {
  filename: string;
  mediaType: string;
  byteSize: number;
  sha256: string;
};

export type PrepareHelpArticleUploadInput = HelpArticleMetadata & {
  files: HelpFileManifestItem[];
};

export type HelpPublicationApi = {
  prepare: (input: PrepareHelpArticleUploadInput) => Promise<HelpUpload>;
  publish: (uploadId: string) => Promise<HelpArticle>;
};

export class HelpPublicationCancelledError extends Error {
  constructor() {
    super(
      "Publishing stopped because the selected customer changed. Review the article and try again.",
    );
    this.name = "HelpPublicationCancelledError";
  }
}

type UploadFileOptions = {
  idToken: string;
  selectedCustomerId: string;
  fetcher?: typeof fetch;
  maxAttempts?: number;
};

const wait = (durationMs: number) =>
  new Promise((resolve) => window.setTimeout(resolve, durationMs));

export const sha256HelpFile = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

export const uploadHelpFile = async (
  file: File,
  target: HelpUploadTarget,
  {
    idToken,
    selectedCustomerId,
    fetcher = fetch,
    maxAttempts = 3,
  }: UploadFileOptions,
): Promise<void> => {
  let lastStatus: number | null = null;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await fetcher(target.url, {
        method: "PUT",
        headers: {
          "Content-Type": target.mediaType,
          ...(target.checksumSha256
            ? { "x-amz-checksum-sha256": target.checksumSha256 }
            : {}),
          ...(target.requiresAuth
            ? {
                "X-Atomic-IdToken": idToken,
                "X-Atomic-CustomerId": selectedCustomerId,
              }
            : {}),
        },
        body: file,
      });
      if (response.ok) return;

      lastStatus = response.status;
      const retryable =
        response.status === 408 ||
        response.status === 429 ||
        response.status >= 500;
      if (!retryable) break;
    } catch (error) {
      lastError = error;
    }

    if (attempt < maxAttempts) {
      await wait(150 * attempt);
    }
  }

  const reason = lastStatus
    ? `server returned ${lastStatus}`
    : lastError instanceof Error
      ? lastError.message
      : "network request failed";
  throw new Error(`Could not upload ${file.name}: ${reason}.`);
};

export const publishHelpBundle = async ({
  metadata,
  files,
  api,
  checksum = sha256HelpFile,
  upload,
  onProgress,
  assertCustomerIsCurrent,
}: {
  metadata: HelpArticleMetadata;
  files: File[];
  api: HelpPublicationApi;
  checksum?: (file: File) => Promise<string>;
  upload: (file: File, target: HelpUploadTarget) => Promise<void>;
  onProgress?: (completed: number, total: number) => void;
  assertCustomerIsCurrent?: () => void;
}): Promise<HelpArticle> => {
  const validation = validateHelpBundle(files);
  if (validation.errors.length > 0) {
    throw new Error(validation.errors.join(" "));
  }

  const manifest = await Promise.all(
    files.map(async (file) => ({
      filename: file.name,
      mediaType:
        canonicalHelpMediaType(file.name) ||
        file.type ||
        "application/octet-stream",
      byteSize: file.size,
      sha256: await checksum(file),
    })),
  );
  assertCustomerIsCurrent?.();
  const pendingUpload = await api.prepare({ ...metadata, files: manifest });
  const targetsByName = new Map(
    pendingUpload.targets.map((target) => [target.filename, target]),
  );

  for (const [index, file] of files.entries()) {
    assertCustomerIsCurrent?.();
    const target = targetsByName.get(file.name);
    if (!target) {
      throw new Error(
        `The server did not provide an upload target for ${file.name}.`,
      );
    }
    await upload(file, target);
    onProgress?.(index + 1, files.length);
  }

  assertCustomerIsCurrent?.();
  return api.publish(pendingUpload.uploadId);
};
